import type {
  ClientAccountDetailV2,
  ClientAccountsAdminV2Row
} from '../services/client-accounts-admin-v2.service'

/**
 * El vocabulario de la pantalla de CUENTAS DE LA APP.
 *
 * Vive aparte del servicio por lo mismo que en el resto de la ola: los componentes y los
 * hooks hablan de filtros y de estado, no de query strings.
 */

export type ClientAccountRow = ClientAccountsAdminV2Row
export type ClientAccountDetail = ClientAccountDetailV2

/**
 * El ESTADO de una cuenta, derivado en el cliente a partir de tres campos.
 *
 * No viaja en el contrato a propósito: es una lectura de presentación —lo que la columna
 * pinta— y componerla en el servidor obligaría a versionar el contrato cada vez que la
 * pantalla quiera distinguir un caso más. Los tres campos crudos ya están.
 *
 * El ORDEN de la derivación importa y no es alfabético: bloqueada gana sobre todo (es la
 * razón por la que no entra aunque tenga inmuebles), y «nunca ingresó» gana sobre activa
 * porque es lo accionable — a esa persona se le puede reenviar la invitación.
 */
export const CLIENT_ACCOUNT_STATUS = {
  BLOCKED: 'blocked',
  NEVER_SIGNED_IN: 'never-signed-in',
  ACTIVE: 'active'
} as const

export type ClientAccountStatus = (typeof CLIENT_ACCOUNT_STATUS)[keyof typeof CLIENT_ACCOUNT_STATUS]

export function clientAccountStatusOf(row: {
  active: boolean
  blockedAt: string | null
  signedIn: boolean
}): ClientAccountStatus {
  if (row.blockedAt || !row.active) return CLIENT_ACCOUNT_STATUS.BLOCKED
  if (!row.signedIn) return CLIENT_ACCOUNT_STATUS.NEVER_SIGNED_IN
  return CLIENT_ACCOUNT_STATUS.ACTIVE
}

/** Copy en tuteo neutro, como el resto del back-office. */
export const CLIENT_ACCOUNT_STATUS_LABELS: Record<ClientAccountStatus, string> = {
  [CLIENT_ACCOUNT_STATUS.BLOCKED]: 'Bloqueada',
  [CLIENT_ACCOUNT_STATUS.NEVER_SIGNED_IN]: 'Sin ingresar',
  [CLIENT_ACCOUNT_STATUS.ACTIVE]: 'Activa'
}

/**
 * Los tres atajos de la barra de tarjetas.
 *
 * Cada uno se traduce a un filtro del allowlist del servidor. **No son excluyentes**: una
 * cuenta bloqueada puede además no haber ingresado nunca, y por eso la pantalla los pinta
 * como tarjetas y no como chips de faceta.
 */
export const CLIENT_ACCOUNT_QUICK_FILTERS = {
  ALL: 'all',
  NEVER_SIGNED_IN: 'never-signed-in',
  BLOCKED: 'blocked'
} as const

export type ClientAccountQuickFilter =
  (typeof CLIENT_ACCOUNT_QUICK_FILTERS)[keyof typeof CLIENT_ACCOUNT_QUICK_FILTERS]

/**
 * Traduce el atajo al filtro del ADR 0020 que el servidor sabe leer.
 *
 * `$null` sobre una fecha es lo que hace útil esta pantalla: «nunca ingresaron» es la
 * cola de trabajo de quien empuja la adopción, y «bloqueadas» la lista que soporte mira.
 */
export function quickFilterToServerFilters(
  quick: ClientAccountQuickFilter
): Record<string, unknown> | undefined {
  if (quick === CLIENT_ACCOUNT_QUICK_FILTERS.NEVER_SIGNED_IN) {
    return { firstAccessAt: { $null: 'true' } }
  }
  if (quick === CLIENT_ACCOUNT_QUICK_FILTERS.BLOCKED) {
    return { blockedAt: { $null: 'false' } }
  }
  return undefined
}

/**
 * Por qué murió un acceso, en palabras.
 *
 * Se traduce por CÓDIGO, nunca por el texto que manda el servidor: el código es contrato
 * y el texto es copy. Un motivo desconocido cae al genérico en vez de romper la fila —
 * las filas viejas pueden traer valores fuera del catálogo actual.
 */
export const GRANT_REVOCATION_LABELS: Record<string, string> = {
  admin: 'Revocado a mano',
  negotiation_expired: 'La negociación venció',
  negotiation_rescinded: 'La negociación se rescindió',
  delivery_completed: 'Entrega completada',
  inactivity: 'Sin actividad'
}

export function grantRevocationLabel(reason: string | null): string {
  if (!reason) return 'Revocado'
  return GRANT_REVOCATION_LABELS[reason] ?? 'Revocado'
}

/** Cómo se presenta la relación del cliente con la negociación que originó el acceso. */
export function grantClientRoleLabel(principal: boolean | null): string {
  if (principal === true) return 'Titular principal'
  if (principal === false) return 'Copropietario'
  return 'Titularidad sin resolver'
}

/**
 * Qué pasó, en palabras, para la pestaña «Actividad».
 *
 * Mismo criterio: se traduce el `action`, que es contrato estable. Una acción que no esté
 * acá NO se muestra cruda (`client_account.algo`): cae a un texto legible que conserva la
 * pista (ver `clientAccountEventLabel`). Un evento sin etiqueta sigue siendo información, y
 * esconderlo es lo que hace que una bitácora mienta por omisión.
 */
export const CLIENT_ACCOUNT_EVENT_LABELS: Record<string, string> = {
  'client_account.created': 'Se creó la cuenta',
  'client_account.invited': 'Se envió la invitación',
  /* Lo hace el CLIENTE, no el equipo. */
  'client_account.first_access': 'Entró por primera vez',
  /* Lo hace el CLIENTE desde la app; la aceptación sale de `client_legal_acceptances`. */
  'client_account.legal_accepted': 'Aceptó los textos legales',
  'client_account.blocked': 'Se bloqueó la cuenta',
  'client_account.reactivated': 'Se reactivó la cuenta',
  'client_account.email_changed': 'Se corrigió el correo',
  'client_account.sessions_revoked': 'Se cerraron sesiones',
  'client_account.first_run_reset': 'Se reinició la cuenta para una nueva revisión',
  'client_grant.created': 'Se concedió un inmueble',
  'client_grant.revoked': 'Se revocó un inmueble',
  'client_grant.revoked_negotiation_expired': 'Se revocó: la negociación venció',
  'client_grant.revoked_negotiation_rescinded': 'Se revocó: la negociación se rescindió',
  'client_grant.revoked_negotiation_paused': 'Se pausó el acceso: la negociación se pausó',
  'client_grant.restored_negotiation_reactivated':
    'Se restituyó el acceso: la negociación se reactivó',
  'client_grant.revoked_inactivity': 'Se revocó por falta de actividad',
  'client_grant.expiry_scheduled_delivery': 'Se programó el vencimiento tras la entrega'
}

/**
 * «client_account.some_new_thing» → «some new thing». Es lo único que se rescata de una acción
 * desconocida: el prefijo de dominio y los guiones bajos son del código, no de quien lee.
 */
function humanizeAction(action: string): string {
  const last = action.split('.').pop() ?? action
  return last.replaceAll('_', ' ').trim()
}

export function clientAccountEventLabel(action: string): string {
  const known = CLIENT_ACCOUNT_EVENT_LABELS[action]
  if (known) return known
  const hint = humanizeAction(action)
  return hint ? `Otro movimiento (${hint})` : 'Otro movimiento'
}
